/**
 * Pure reducers for document-level ops on the IR Document.
 *
 * Phase FB.3 of docs/design/frontend-mv-architecture-260524.md.
 * Phase FB.8: returns `{ir, dirtyPages}`. Doc-level ops emit
 * `dirtyPages: new Set([0])` — page 0 is the well-known doc-level
 * sentinel that the dispatcher routes to doc-level subscribers
 * (statusbar, inspector). Per §P.3.
 *
 * Layer: reducers.
 *
 * Reducers in this file:
 *   - setTitleReducer        — set-title (Document.title)
 *   - setLangReducer         — set-lang (Document.language)
 *   - setAttestationReducer  — set-attestation (Document.attestation)
 *   - clearAllTagsReducer    — clear-all-tags (empty Document.blocks)
 *
 * All three are trivial single-field overwrites — they exist as separate
 * reducers (not folded into one) because each carries its own action
 * type, and a future reducer extension (e.g., validation of attestation
 * shape, language-tag normalisation) attaches to one without bleeding
 * into the others.
 *
 * @module reducers/doc-reducers
 */
export function setTitleReducer(ir, op) {
    return { ir: { ...ir, title: op.after }, dirtyPages: new Set([0]) };
}
/**
 * clear-all-tags — empty ALL logical structure. Returns an IR with `blocks: []`
 * while preserving every other Document field via `...ir` — critically `pages`
 * and `mcids` (the G-3 constraint: new hand-authored elements anchor their bbox
 * to the still-present MCID/page geometry, so wiping those would make
 * hand-authoring from scratch impossible).
 *
 * `dirtyPages` is the FULL-DOC set — the doc-level sentinel `0` plus every page
 * that carried a block before the clear — so every previously-populated page
 * re-renders (its overlay boxes removed) and doc-level subscribers refresh.
 */
export function clearAllTagsReducer(ir, _op) {
    const dirtyPages = new Set([0]);
    for (const b of ir.blocks) {
        if (typeof b.page === "number")
            dirtyPages.add(b.page);
    }
    return { ir: { ...ir, blocks: [] }, dirtyPages };
}
export function setLangReducer(ir, op) {
    return { ir: { ...ir, language: op.after }, dirtyPages: new Set([0]) };
}
export function setAttestationReducer(ir, op) {
    return { ir: { ...ir, attestation: op.after }, dirtyPages: new Set([0]) };
}
